import { useEffect, useRef, useState } from 'react';

/**
 * Lusion-feeling liquid cursor — canvas with trail.
 *
 * Eight followers chase the pointer with progressively softer spring physics,
 * drawn as soft circles in paper color. Each frame we DON'T fully clear the
 * canvas — we paint a semi-transparent black overlay instead, so previous
 * frames bleed through. Combined with CSS `blur() contrast()`, this produces
 * a continuous liquid trail that fuses and dissolves rather than discrete
 * blobs. The radius of the head follower grows with pointer velocity, so
 * fast movement visibly *stretches* the blob.
 *
 * mix-blend-difference inverts on any background. Hover-fine pointers only;
 * reduced-motion disables.
 */

type Follower = { x: number; y: number; vx: number; vy: number; lerp: number; radius: number };

const FOLLOWERS: Array<Pick<Follower, 'lerp' | 'radius'>> = [
  { lerp: 0.55, radius: 24 },
  { lerp: 0.4, radius: 22 },
  { lerp: 0.28, radius: 19 },
  { lerp: 0.2, radius: 16 },
  { lerp: 0.14, radius: 14 },
  { lerp: 0.1, radius: 12 },
  { lerp: 0.075, radius: 10 },
  { lerp: 0.055, radius: 9 },
];

export function CursorTracker() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const caretRef = useRef<HTMLDivElement | null>(null);
  const [variant, setVariant] = useState<'default' | 'pointer' | 'text' | 'hidden'>('hidden');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canHover || reduce) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setVariant('default');

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    let mouseX = -200;
    let mouseY = -200;
    let lastMouseX = -200;
    let lastMouseY = -200;
    let speed = 0;

    const followers: Follower[] = FOLLOWERS.map((f) => ({
      x: -200,
      y: -200,
      vx: 0,
      vy: 0,
      lerp: f.lerp,
      radius: f.radius,
    }));

    let scaleMul = 1;
    let raf = 0;

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Velocity update (smoothed)
      const dx = mouseX - lastMouseX;
      const dy = mouseY - lastMouseY;
      const instantSpeed = Math.hypot(dx, dy);
      speed = speed * 0.7 + instantSpeed * 0.3;
      lastMouseX = mouseX;
      lastMouseY = mouseY;

      // Always paint a soft scrim — never clearRect. This way recent paper
      // pixels accumulate into a stable blob: when moving the scrim is
      // strong enough to wash the tail, when still it's gentle enough that
      // the head stays solid. NEVER break the additive chain.
      const scrimAlpha = 0.05 + Math.min(speed * 0.01, 0.14);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgba(0, 0, 0, ${scrimAlpha})`;
      ctx.fillRect(0, 0, w, h);

      // Composite paper circles additively — under blur(10px) contrast(14)
      // additive stacking is what holds the blob solid.
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = 'rgba(237, 237, 235, 1)';

      const stretch = Math.min(speed * 0.22, 14);
      const dirX = speed > 0.1 ? dx / instantSpeed : 0;
      const dirY = speed > 0.1 ? dy / instantSpeed : 0;

      for (let i = 0; i < followers.length; i++) {
        const f = followers[i]!;
        f.x += (mouseX - f.x) * f.lerp;
        f.y += (mouseY - f.y) * f.lerp;
        // tail offset along motion vector (negative = behind head)
        const offset = i * stretch * 0.7;
        const px = f.x - dirX * offset;
        const py = f.y - dirY * offset;
        const r = f.radius * scaleMul;
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const onMove = (e: PointerEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const textInput = target.closest('input, textarea, [contenteditable="true"]');
      const interactive = target.closest(
        '[data-magnetic="true"], a, button, [role="button"], label[for], summary, select',
      );
      if (textInput) {
        setVariant('text');
        scaleMul = 0.0001;
      } else if (interactive) {
        setVariant('pointer');
        scaleMul = 2.4;
      } else {
        setVariant('default');
        scaleMul = 1;
      }
    };
    const onLeave = () => setVariant('hidden');
    const onEnter = () => setVariant('default');

    window.addEventListener('pointermove', onMove);
    document.addEventListener('pointerleave', onLeave);
    document.addEventListener('pointerenter', onEnter);

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('pointerenter', onEnter);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Text caret (separate, narrow vertical bar when over inputs)
  useEffect(() => {
    if (variant !== 'text') return;
    const el = caretRef.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      el.style.transform = `translate3d(${e.clientX - 1}px, ${e.clientY - 11}px, 0)`;
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [variant]);

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[300] hidden md:block"
        style={{
          filter: 'blur(10px) contrast(14)',
          mixBlendMode: 'difference',
          opacity: variant === 'hidden' || variant === 'text' ? 0 : 1,
          transition: 'opacity 220ms ease',
        }}
      />
      <div
        ref={caretRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[301] hidden md:block mix-blend-difference"
        style={{
          width: 2,
          height: 22,
          background: '#ededeb',
          opacity: variant === 'text' ? 1 : 0,
          transition: 'opacity 200ms ease',
        }}
      />
    </>
  );
}
